import { useEffect, useState } from 'react';
import { paymentAPI, receiptsAPI, getApiErrorMessage } from '@matix/api-client';
import type { PaymentType } from '@matix/types';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { XIcon } from '../../components/icons';

/**
 * Payment-method edit dialog — Phase 10.9.
 *
 * Lets a cashier change a receipt's recorded payment type after the fact.
 *
 * NO manager PIN since 2026-08-31 — the change is audited to
 * `payment_method_audit` but needs no second person. Server enforces:
 *   - receipt is within env.POS_PAYMENT_CORRECTION_WINDOW_DAYS days
 *   - new payment type belongs to the same subsidiary
 */

interface Props {
  receiptId: number;
  receiptCode: string;
  currentPaymentTypeId: number | null;
  currentPaymentLabel: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export function PaymentMethodEditDialog({
  receiptId,
  receiptCode,
  currentPaymentTypeId,
  currentPaymentLabel,
  onClose,
  onSaved,
}: Props) {
  const { t } = useLang();
  const [types, setTypes] = useState<PaymentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newTypeId, setNewTypeId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    paymentAPI.listTypes()
      .then((res) => {
        if (cancelled) return;
        const filtered = res.payment_types.filter((p) => p.code !== 0 && p.code !== -1);
        setTypes(filtered);
      })
      .catch((err) => toast(getApiErrorMessage(err), 'error'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const canSubmit =
    !submitting &&
    newTypeId != null &&
    newTypeId !== currentPaymentTypeId;

  async function handleSubmit() {
    if (!canSubmit || newTypeId == null) return;
    setSubmitting(true);
    setError(null);
    try {
      await receiptsAPI.patchPaymentMethod(receiptId, {
        new_payment_type_id: newTypeId,
        reason: reason.trim() || undefined,
      });
      toast(t({ en: 'Payment method updated', ar: 'تم تحديث طريقة الدفع' }), 'success');
      onSaved();
    } catch (err) {
      const msg = getApiErrorMessage(err);
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay show" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--warning-light)', color: 'var(--warning)' }}
          >
            ⇄
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{t({ en: 'Change payment method', ar: 'تغيير طريقة الدفع' })}</div>
            <div className="modal-header-name-ar">
              {t({ en: `Receipt ${receiptCode} · currently ${currentPaymentLabel ?? '—'}`, ar: `فاتورة ${receiptCode} · حاليًا ${currentPaymentLabel ?? '—'}` })}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading ? (
            <div style={{ padding: 20, color: 'var(--text-muted)' }}>{t({ en: 'Loading payment types…', ar: 'جارٍ تحميل طرق الدفع…' })}</div>
          ) : (
            <>
              <div>
                <label className="mod-group-title" style={{ display: 'block', marginBottom: 6 }}>
                  {t({ en: 'New payment method', ar: 'طريقة الدفع الجديدة' })}
                </label>
                <select
                  className="kitchen-note"
                  value={newTypeId ?? ''}
                  onChange={(e) => setNewTypeId(e.target.value ? parseInt(e.target.value, 10) : null)}
                  style={{ minHeight: 0, padding: '10px 12px' }}
                >
                  <option value="">{t({ en: '— Select —', ar: '— اختر —' })}</option>
                  {types.map((p) => (
                    <option key={p.id} value={p.id} disabled={p.id === currentPaymentTypeId}>
                      {p.name}{p.id === currentPaymentTypeId ? t({ en: ' (current)', ar: ' (الحالية)' }) : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/*
                The Manager PIN field was removed 2026-08-31. The change is
                still audited to `payment_method_audit`; it simply no longer
                needs a second person. The server also stopped running the PIN
                query, which could not execute at all on a tenant whose
                `Security_Users` lacks `RoleID` / `IsActive`.
              */}

              <div>
                <label className="mod-group-title" style={{ display: 'block', marginBottom: 6 }}>
                  {t({ en: 'Reason (optional)', ar: 'السبب (اختياري)' })}
                </label>
                <textarea
                  className="kitchen-note"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  placeholder={t({ en: 'Why is this change being made?', ar: 'لماذا يتم إجراء هذا التغيير؟' })}
                />
              </div>

              {error && (
                <div style={{ color: 'var(--danger)', fontSize: 13 }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <div />
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              {t({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            <button
              type="button"
              className="btn-primary-modal"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Confirm', ar: 'تأكيد' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
